import React from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ToolbarActionProps extends React.ComponentProps<typeof Button> {
    /** Non-null means the action is unavailable, and this is why. */
    blockedReason: string | null;
    /** Shown when the action is available; omit for buttons that carry a label. */
    tooltip?: string;
    /** Id of the hidden element carrying `blockedReason`; must be unique. */
    reasonId: string;
}

/**
 * A report toolbar button that says why it is unavailable instead of going
 * quietly dead.
 *
 * `aria-disabled` rather than the native attribute: a natively disabled button
 * leaves the a11y tree, takes its reason with it, and — via the base
 * `disabled:pointer-events-none` — never fires the hover that would open the
 * tooltip in the first place. The reason also rides in a hidden span so it
 * exists whether or not the tooltip happens to be open; the span sits *outside*
 * the button so it is read as the description and not folded into the name.
 *
 * Expects a `TooltipProvider` above it.
 */
export const ToolbarAction: React.FC<ToolbarActionProps> = ({
    blockedReason,
    tooltip,
    reasonId,
    onClick,
    className,
    children,
    ...props
}) => {
    const content = blockedReason ?? tooltip;

    return (
        <>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        {...props}
                        aria-disabled={blockedReason ? true : undefined}
                        aria-describedby={blockedReason ? reasonId : undefined}
                        onClick={(event) => {
                            if (blockedReason) {
                                event.preventDefault();
                                return;
                            }
                            onClick?.(event);
                        }}
                        className={cn(className, blockedReason && 'cursor-not-allowed opacity-50')}
                    >
                        {children}
                    </Button>
                </TooltipTrigger>
                {content ? (
                    <TooltipContent side="bottom" className="max-w-56 text-center leading-5">
                        {content}
                    </TooltipContent>
                ) : null}
            </Tooltip>
            {blockedReason ? (
                <span id={reasonId} className="sr-only">
                    {blockedReason}
                </span>
            ) : null}
        </>
    );
};
